import {
  ArrowUpOnSquareIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  LinkIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { Integrations as IntegrationsIcon } from '@praetorian-chariot/ui';
import { ScanText } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { toast } from 'sonner';

import { AffiliationIcon } from '@/components/icons/Affiliation.icon';
import { Modal } from '@/components/Modal';
import { useRunAffiliation } from '@/hooks/affiliation';
import { useIntegration } from '@/hooks/useIntegration';
import { useBulkReRunJob } from '@/hooks/useJobs';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { Asset, Risk } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';

interface RiskActionProps {
  risks: Risk[];
  impactedAssets?: Asset[];
  onSuccess?: () => void;
  onComment: () => void;
  showTicketActions?: boolean;
  onCreateTicket?: (risks: Risk[]) => void;
}

export function useRiskActions({
  impactedAssets,
  risks,
  onSuccess,
  onComment,
  showTicketActions,
  onCreateTicket,
}: RiskActionProps) {
  const {
    modal: { ticket },
  } = useGlobalState();
  const { mutateAsync: bulkReRunJob } = useBulkReRunJob();
  const { mutateAsync: runAffiliation } = useRunAffiliation();

  const { isPraetorianUser } = useAuth();

  const {
    data: { connectedIntegrations },
  } = useIntegration({ enabled: showTicketActions });
  const isJiraConnected = Boolean(
    connectedIntegrations?.find(integration => integration.member === 'jira')
  );

  const handleBulkRescan = async () => {
    await bulkReRunJob(
      risks.map(risk => {
        return {
          jobKey: risk.key,
        };
      })
    );

    onSuccess?.();
  };

  const handleExport = () => {
    const jsonData = JSON.stringify(risks, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'exported_Vulnerabilities.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${risks.length} risks to JSON`);
  };

  function handleAffiliation() {
    risks.forEach(risk => {
      runAffiliation(risk.key);
    });
  }

  const disableAffiliation =
    !isPraetorianUser ||
    !(impactedAssets
      ? impactedAssets?.some(
          asset => asset.private === false && asset.source !== 'account'
        )
      : true);

  const moreActions: DropdownOption[] = [
    {
      label: 'Confirm Asset Affiliation',
      icon: <AffiliationIcon className="text-5" />,
      onClick: handleAffiliation,
      disabled: disableAffiliation,
    },
    {
      label: 'Export',
      value: 'export',
      icon: <ArrowUpOnSquareIcon className="size-5" />,
      onClick: handleExport,
    },
    {
      label: 'Add Note',
      value: 'comment',
      icon: <ChatBubbleOvalLeftEllipsisIcon className="size-5" />,
      onClick: onComment,
    },
    {
      label: 'Rescan',
      value: 'scan',
      icon: <ScanText className="size-5" />,
      onClick: handleBulkRescan,
    },
    ...(showTicketActions
      ? isJiraConnected
        ? [
            {
              label: 'Create New Ticket',
              value: 'new-ticket',
              icon: <PlusCircleIcon />,
              onClick: async () => {
                onCreateTicket?.(risks);
              },
            },
            {
              label: 'Associate Existing Ticket',
              value: 'associate-ticket',
              icon: <LinkIcon />,
              onClick: () => {
                ticket.associateTicket.onRiskKeysChange(
                  risks.map(risk => risk.key)
                );
                ticket.associateTicket.onOpenChange(true);
              },
            },
          ]
        : [
            {
              label: 'Integrate Jira Cloud',
              value: 'integrate-jira-cloud',
              icon: <IntegrationsIcon />,
              onClick: () => {
                ticket.setup.onOpenChange(true);
              },
            },
          ]
      : []),
  ];

  return {
    moreActions,
  };
}

interface CommentModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => void;
  children?: ReactNode;
}

/**
 * @deprecated This component has been migrated to shared NoteModal.
 * Migration Date: 2025-10-23
 * Replaced By: @/components/modals/NoteModal/NoteModal
 *
 * This export is kept temporarily for backwards compatibility.
 * Safe to remove after all imports have been updated.
 *
 * Remaining known uses (being actively migrated):
 * - None - migration complete!
 *
 * Migration pattern:
 * ```typescript
 * // OLD:
 * import { CommentModal as NoteModal } from '@/hooks/useRiskActions';
 * <NoteModal open={open} onClose={onClose} onSubmit={onSubmit} />
 *
 * // NEW:
 * import { NoteModal } from '@/components/modals/NoteModal/NoteModal';
 * <NoteModal
 *   open={open}
 *   onClose={onClose}
 *   onSubmit={onSubmit}
 *   resourceType="Risk"
 *   mode="simple"
 *   presentation="modal"
 *   selectedCount={selectedCount}
 * />
 * ```
 */
export function CommentModal({
  open,
  onClose,
  onSubmit,
  children,
}: CommentModalProps) {
  const [comment, setComment] = useState('');

  const handleSubmit = () => {
    if (!comment.trim()) return;
    onSubmit(comment);
    setComment('');
    onClose();
  };

  return (
    <Modal
      size="xl"
      title="New Vulnerability Note"
      open={open}
      onClose={onClose}
      footer={{
        text: 'Add Note',
        onClick: handleSubmit,
      }}
    >
      <textarea
        value={comment}
        onChange={e => setComment(e.target.value)}
        className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-brand min-h-[200px] w-full rounded-md border p-4 focus:ring-2 focus:outline-none"
        placeholder="Add Note..."
      />
      {children}
    </Modal>
  );
}
